import React, { useState, useEffect, useRef } from "react";
import "./Footer.css";
import ContactForm from "./ContactForm";
import { getFooterTexts, subscribeToFooterTexts } from "../services/footerDataService";
import { Capacitor } from "@capacitor/core";

function Footer() {
  const isIOSApp = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
  const [activeModal, setActiveModal] = useState(null);
  const [footerTexts, setFooterTexts] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [copiedText, setCopiedText] = useState("");
  const modalRef = useRef(null);

  const USE_HTML = false;

  const PAYPAL_URL =
    "https://www.paypal.com/donate/?hosted_button_id=4VK7M7ELWD9J6&locale.x=de_DE";

  const openModal = (type) => setActiveModal(type);
  const closeModal = () => setActiveModal(null);

  useEffect(() => {
    let mounted = true;
    let unsubscribe = null;

    const loadAndSubscribe = async () => {
      setIsLoading(true);
      setLoadError("");

      try {
        const cached = localStorage.getItem("footerTexts_cache_v1");
        if (cached && mounted) {
          setFooterTexts(JSON.parse(cached));
        }
      } catch (e) {
        console.warn("Footer Cache konnte nicht gelesen werden:", e);
      }

      try {
        const initialData = await getFooterTexts();

        if (!mounted) return;

        setFooterTexts(initialData);

        try {
          localStorage.setItem("footerTexts_cache_v1", JSON.stringify(initialData));
        } catch (e) {
          console.warn("Footer Cache konnte nicht gespeichert werden:", e);
        }

        unsubscribe = await subscribeToFooterTexts(
          (liveData) => {
            if (!mounted) return;

            setFooterTexts(liveData || {});

            try {
              localStorage.setItem("footerTexts_cache_v1", JSON.stringify(liveData || {}));
            } catch (e) {
              console.warn("Footer Cache konnte nicht gespeichert werden:", e);
            }

            setLoadError("");
            setIsLoading(false);
          },
          (error) => {
            console.error("Fehler im Footer-Live-Listener:", {
              name: error?.name || null,
              message: error?.message || null,
              stack: error?.stack || null,
              full: error,
            });

            if (mounted) {
              setLoadError("Footer-Inhalte konnten nicht geladen werden.");
              setIsLoading(false);
            }
          }
        );
      } catch (error) {
        console.error("Fehler beim Laden der Footer-Texte:", {
          name: error?.name || null,
          message: error?.message || null,
          stack: error?.stack || null,
          full: error,
        });

        if (mounted) {
          setLoadError("Footer-Inhalte konnten nicht geladen werden.");
          setIsLoading(false);
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadAndSubscribe();

    return () => {
      mounted = false;

      if (typeof unsubscribe === "function") {
        const maybePromise = unsubscribe();
        if (maybePromise?.catch) {
          maybePromise.catch((error) => {
            console.error("Fehler beim Aufräumen des Footer-Listeners:", error);
          });
        }
      }
    };
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    if (activeModal && modalRef.current) modalRef.current.focus();
  }, [activeModal]);

 const handleCopy = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      window.prompt("E-Mail-Adresse kopieren:", text);
    }

    setCopiedText(text);

    setTimeout(() => {
      setCopiedText("");
    }, 2000);
  } catch (err) {
    console.error("Copy fehlgeschlagen:", err);
  }
};

const linkifyLine = (line) => {
  const parts = String(line || "").split(
    /(https?:\/\/[^\s]+|[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/g
  );

  return parts.map((part, i) => {
    if (!part) return null;

    if (/^https?:\/\/[^\s]+$/.test(part)) {
      return (
        <a key={i} href={part} target="_blank" rel="noreferrer">
          {part}
        </a>
      );
    }

    if (/^[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+$/.test(part)) {
      return (
        <span
          key={i}
          className="copy-email"
          onClick={() => handleCopy(part)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleCopy(part);
          }}
          role="button"
          tabIndex={0}
        >
          {part}
        </span>
      );
    }

    return part;
  });
};

  const renderPlainText = (text) => {
    const normalized = String(text || "").replace(/\r\n/g, "\n").trim();
    const paragraphs = normalized.split(/\n\s*\n/).filter(Boolean);

    return (
      <div className="legal-text">
        {paragraphs.map((para, idx) => {
          const lines = para.split("\n");
          return (
            <p key={idx}>
              {lines.map((line, i) => {
                const trimmed = line.trim();
                const isParagraphTitle = /^§\s*\d+/i.test(trimmed);

                return (
                  <React.Fragment key={i}>
                    {isParagraphTitle ? (
                      <strong className="legal-paragraph-title">{trimmed}</strong>
                    ) : (
                      linkifyLine(trimmed)
                    )}
                    {i < lines.length - 1 && <br />}
                  </React.Fragment>
                );
              })}
            </p>
          );
        })}
      </div>
    );
  };

  const renderModalContent = () => {
    if (activeModal === "kontakt") {
      return (
        <ContactForm endpointUrl="https://sendcontactmessage-t2l67uvjtq-ey.a.run.app" />
      );
    }

    if (loadError) return <p>{loadError}</p>;
    if (isLoading) return <p>Inhalt wird geladen...</p>;

    const content = footerTexts[activeModal];
    if (!content) return <p>Inhalt wird geladen...</p>;

    if (USE_HTML) return <div dangerouslySetInnerHTML={{ __html: content }} />;

    return renderPlainText(content);
  };

  const renderDonationButton = () => {
  if (isIOSApp) return null;
  if (activeModal !== "spenden") return null;

  return (
    <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
      <a
        className="paypal-button"
        href={PAYPAL_URL}
        target="_blank"
        rel="noreferrer"
      >
        Jetzt via PayPal spenden
      </a>
    </div>
  );
};

  return (
    <>
      <footer className="footer">
        <div className="footer-links">
          <span onClick={() => openModal("impressum")}>Impressum</span>
          <span onClick={() => openModal("kontakt")}>Kontakt</span>
          <span onClick={() => openModal("datenschutz")}>Datenschutz</span>
          <span onClick={() => openModal("agb")}>Nutzungsbedingungen</span>
          {!isIOSApp && (
  <span onClick={() => openModal("spenden")}>Spenden</span>
)}
          <span onClick={() => openModal("entstehung")}>Über uns</span>
        </div>

        <p className="footer-text">
          © {new Date().getFullYear()} Edens Ernte – Gemeinsam nachhaltig teilen.
        </p>
      </footer>

      {activeModal && (
        <div className="footer-modal-overlay" onClick={closeModal}>
          <div
            className="footer-modal-content"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            ref={modalRef}
          >
            {renderModalContent()}
            {renderDonationButton()}

            {copiedText && <div className="copy-toast">✅ E-Mail Adresse kopiert</div>}

            <button
              className="green-button"
              onClick={closeModal}
              style={{ marginTop: 16 }}
            >
              Schließen
            </button>
          </div>
        </div>
      )}
    </>
  );
}
export default Footer;